import axios from 'axios';
import { useRef, useState } from 'react';
import { useBusyWhile } from '@/context/busy-context';
import { getApiErrorFields, getApiErrorMessage } from '@utils/api-error';
import { buildPostHeaders } from '@utils/http';
import type { IApiEnvelope, IApiSubmitResultBase } from '@/types/api';

export interface IUseApiSubmitResult<TValues, TResult extends IApiSubmitResultBase> {
    submit: (values: TValues) => Promise<TResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

/**
 * POSTs form values to a new-dash API action. `toPayload` shapes the request body and
 * `toResult` maps the success envelope's `results` onto the page's result type. A 400
 * with a structured body becomes `{ ok: false, message, errors }`.
 *
 * Re-entrancy is guarded with a ref so a double click before re-render still sends once.
 */
export const useApiSubmit = <TValues, TResults, TResult extends IApiSubmitResultBase>(
    url: string,
    toPayload: (values: TValues) => unknown,
    toResult: (results: TResults) => Omit<TResult, keyof IApiSubmitResultBase>,
    failureMessage = 'Submission failed. Please try again.',
): IUseApiSubmitResult<TValues, TResult> => {
    const inFlight = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useBusyWhile(isSubmitting);

    const submit = async (values: TValues): Promise<TResult> => {
        if (inFlight.current) return { ok: false } as TResult;

        inFlight.current = true;
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const { data } = await axios.post<IApiEnvelope<TResults>>(url, toPayload(values), {
                headers: buildPostHeaders(),
                withCredentials: true,
            });

            return { ok: data.success, ...toResult(data.results) } as TResult;
        } catch (err: unknown) {
            const message = getApiErrorMessage(err, failureMessage);
            const errors = getApiErrorFields(err);

            setSubmitError(message);

            return { ok: false, errors, message } as TResult;
        } finally {
            inFlight.current = false;
            setIsSubmitting(false);
        }
    };

    return { submit, isSubmitting, submitError };
};
